import axios from "axios";

/* =========================================================
   BASE URL
========================================================= */

const BASE_URL = "https://nhrc.onrender.com";

/* =========================================================
   AXIOS INSTANCE
========================================================= */

const api = axios.create({
  baseURL: BASE_URL,
  timeout:  300000,       //60000,
});

/* =========================================================
   PUBLIC ROUTES
========================================================= */

const PUBLIC_ROUTES = [
  "/auth/login",
  "/auth/admin/login",
  "/admin/login",
  "/auth/register",
  "/auth/register-admin",
  "/auth/captcha",
  "/auth/verify-captcha",
  "/otp",
  "/password",
  "/contact",

  "/student/all",
  "/student/preview",

  "/membership/personal",
  "/membership/employee-details",
  "/membership/student-university",
  "/membership/student-autonomous",
  "/membership/representative-university",
  "/membership/representative-autonomous",
  "/membership/representative-both",
];

/* =========================================================
   JWT PARSER
========================================================= */

const parseJwt = (token) => {
  try {
    if (!token) return null;
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(window.atob(base64));
  } catch (error) {
    console.log("JWT PARSE ERROR :", error);
    return null;
  }
};

/* =========================================================
   CLEAR TOKENS
========================================================= */

const clearTokens = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("adminToken");
  localStorage.removeItem("hrToken");
  localStorage.removeItem("studentToken");
  localStorage.removeItem("member");
  localStorage.removeItem("loggedInUser");
};

/* =========================================================
   SAVE LOGIN TOKEN
========================================================= */

const saveLoginToken = (token) => {
  try {
    if (!token) return;
    const decoded = parseJwt(token);
    const role = decoded?.role?.replace("ROLE_", "")?.toUpperCase();

    localStorage.removeItem("adminToken");
    localStorage.removeItem("hrToken");
    localStorage.removeItem("studentToken");
    localStorage.setItem("token", token);

    if (role === "ADMIN") {
      localStorage.setItem("adminToken", token);
    } else if (role === "HR" || role === "EMPLOYEE") {
      localStorage.setItem("hrToken", token);
    } else {
      localStorage.setItem("studentToken", token);
    }
  } catch (error) {
    console.log("SAVE TOKEN ERROR :", error);
  }
};

/* =========================================================
   GET TOKEN
========================================================= */

const getToken = (url = "") => {
  if (url.includes("/student/")) {
    const isPublicStudentAPI = url.includes("/student/all") || url.includes("/student/preview");
    if (isPublicStudentAPI) return null;
    return localStorage.getItem("studentToken");
  }

  if (url.includes("/hr/")) {
    return localStorage.getItem("hrToken") || localStorage.getItem("token");
  }

  if (url.includes("/admin/")) {
    return localStorage.getItem("adminToken") || localStorage.getItem("token");
  }

  if (url.includes("/upload/")) {
    const adminToken = localStorage.getItem("adminToken");
    const hrToken = localStorage.getItem("hrToken");
    return adminToken || hrToken || localStorage.getItem("token");
  }

  return localStorage.getItem("token") || null;
};

/* =========================================================
   REQUEST INTERCEPTOR
========================================================= */

api.interceptors.request.use(
  function (config) {
    const url = config.url || "";
    const token = getToken(url);

    const isPublicAPI = PUBLIC_ROUTES.some((route) => url.includes(route));

    if (token && !isPublicAPI) {
      config.headers.Authorization = "Bearer " + token;
    } else {
      delete config.headers.Authorization;
    }

    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    } else {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

/* =========================================================
   RESPONSE INTERCEPTOR
========================================================= */

api.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (!error.response) {
      console.log(
        "🚫 NO RESPONSE RECEIVED for:",
        error.config?.method?.toUpperCase(),
        error.config?.url
      );
    }

    if (error.response && error.response.status === 401) {
      clearTokens();
    }

    return Promise.reject(error);
  }
);

/* =========================================================
   AUTH APIs
========================================================= */

const AuthAPI = {
  login: async function (data) {
    const response = await api.post("/auth/login", {
      email: data.email.trim().toLowerCase(),
      password: data.password.trim(),
      captcha_id: data.captcha_id,
      captcha: data.captcha,
    });

    if (response.data && response.data.token) {
      clearTokens();
      saveLoginToken(response.data.token);
    }

    return response;
  },

  adminLogin: async function (data) {
    const response = await api.post("/admin/login", {
      email: data.email.trim().toLowerCase(),
      password: data.password.trim(),
      captcha_id: data.captcha_id,
      captcha: data.captcha,
    });

    if (response.data && response.data.token) {
      clearTokens();
      saveLoginToken(response.data.token);
    }

    return response;
  },

  register: async function (data) {
    return await api.post("/auth/register", data);
  },

  registerAdmin: async function (data) {
    return await api.post("/auth/register-admin", data);
  },

  refreshToken: async function () {
    return await api.post("/auth/refresh");
  },

  getCaptcha: async function () {
    return await api.get("/auth/captcha");
  },

  verifyCaptcha: async function (data) {
    return await api.post("/auth/verify-captcha", {
      captcha_id: data.captcha_id,
      answer: data.answer,
    });
  },
};

/* =========================================================
   OTP APIs
========================================================= */

const OtpAPI = {
  sendOtp: async function (email, purpose = "REGISTER", candidateType = "employee") {
    return await api.post("/otp/send-otp", {
      email: email,
      purpose: purpose,
      candidate_type: candidateType,
    });
  },

  verifyOtp: async function (data) {
    return await api.post("/otp/verify-otp", {
      email: data.email,
      otp: data.otp,
    });
  },
};


/* =========================================================
   PASSWORD APIs

   ✅ CORRECTED against actual Swagger response:
     POST /password/forgot-password?email=...   (email as QUERY param) — unchanged, already correct
     POST /password/reset-password
       { email, otp, new_password, confirm_password }   (BODY)
   Confirmed via Swagger's own "Try it out" response:
     { "message": "Password updated successfully" }
   There is NO "token" field — the OTP is submitted directly
   here, not via a separate verify-otp step. The earlier
   assumption that this needed a reset token from
   /otp/verify-otp was wrong — fixed below.
========================================================= */

const PasswordAPI = {
  forgotPassword: async function (email) {
    return await api.post(
      "/password/forgot-password?email=" + encodeURIComponent(email)
    );
  },

  resetPassword: async function (data) {
    return await api.post("/password/reset-password", {
      email: data.email,
      otp: data.otp,
      new_password: data.new_password,
      confirm_password: data.confirm_password,
    });
  },
};
/* =========================================================
   MEMBERSHIP APIs
========================================================= */

const MembershipAPI = {
  createPersonal: async function (data) {
    return await api.post("/membership/personal", data);
  },
  createEmployee: async function (data) {
    return await api.post("/membership/employee-details", data);
  },
  createEmployeeDetails: async function (data) {
    return await api.post("/membership/employee-details", data);
  },
  studentUniversity: async function (data) {
    return await api.post("/membership/student-university", data);
  },
  studentAutonomous: async function (data) {
    return await api.post("/membership/student-autonomous", data);
  },
  representativeUniversity: async function (data) {
    return await api.post("/membership/representative-university", data);
  },
  representativeAutonomous: async function (data) {
    return await api.post("/membership/representative-autonomous", data);
  },
  representativeBoth: async function (data) {
    return await api.post("/membership/representative-both", data);
  },
};

/* =========================================================
   ADMIN APIs
========================================================= */

const AdminAPI = {
  dashboard: async function () {
    return await api.get("/admin/dashboard");
  },
  getEmployees: async function () {
    return await api.get("/admin/employees");
  },
  getStudents: async function () {
    return await api.get("/admin/students");
  },
  getRepresentatives: async function () {
    return await api.get("/admin/representatives");
  },
  getMemberById: async function (membershipId) {
    return await api.get("/admin/member/" + membershipId);
  },
  approveMember: async function (membershipId) {
    return await api.put("/admin/members/" + membershipId + "/approve");
  },
  rejectMember: async function (membershipId) {
    return await api.put("/admin/members/" + membershipId + "/reject");
  },
  deleteMember: async function (membershipId) {
    return await api.delete("/admin/members/" + membershipId);
  },
  bulkDeleteMembers: async function (ids) {
    return await api.post("/admin/members/bulk-delete", { membership_ids: ids });
  },
  createBenefit: (payload) => api.post("/admin/member-benefits", payload),
  deleteMultipleBenefits: (ids) => api.delete("/admin/member-benefits", { data: { ids } }),
  getAllBenefits: (category) => api.get(`/admin/member-benefits/${category}`),
  updateBenefit: (benefit_id, payload) => api.put(`/admin/member-benefits/${benefit_id}`, payload),
  deleteBenefit: (benefit_id) => api.delete(`/admin/member-benefits/${benefit_id}`),
  updateBenefitStatus: (benefit_id, status) =>
    api.put(`/admin/member-benefits/${benefit_id}/status`, { is_active: status === "active" }),
};

/* =========================================================
   CONTACT APIs
========================================================= */

const ContactAPI = {
  createContact: async function (data) {
    return await api.post("/contact/", data);
  },
  getAllContacts: async function () {
    return await api.get("/contact/");
  },
  getContactById: async function (id) {
    return await api.get("/contact/" + id);
  },
  deleteContact: async function (id) {
    return await api.delete("/contact/" + id);
  },
};

/* =========================================================
   UPLOAD APIs
========================================================= */

const UploadAPI = {
  uploadFile: async function (formData) {
    return await api.post("/upload/", formData);
  },
  uploadImage: async function (file) {
    const fd = new FormData();
    fd.append("file", file);
    return await api.post("/upload/image", fd);
  },
  uploadPdf: async function (file) {
    const fd = new FormData();
    fd.append("file", file);
    return await api.post("/upload/pdf", fd);
  },
  getUploadedFiles: (membership_id) => api.get(`/upload/${membership_id}`),
};

/* =========================================================
   JOB APIs
========================================================= */

const JobAPI = {
  createJob: async function (data) {
    return await api.post("/admin/create", data);
  },
  updateJob: async function (jobId, data) {
    return await api.put("/admin/update/" + jobId, data);
  },
  deleteJob: async function (jobId) {
    return await api.delete("/admin/delete/" + jobId);
  },
  approveJob: async function (jobId) {
    return await api.put("/admin/approve/" + jobId);
  },
  rejectJob: async function (jobId) {
    return await api.put("/admin/reject/" + jobId);
  },
  getAdminAllJobs: async function () {
    return await api.get("/admin/all");
  },
  getAllJobs: async function () {
    return await api.get("/student/all");
  },
  getJobById: async function (jobId) {
    return await api.get("/student/preview/" + jobId);
  },
  studentPreviewJob: async function (jobId) {
    return await api.get("/student/preview/" + jobId);
  },
  getApplicationsByJob: async function (job_id) {
    return await api.get(`/admin/${job_id}/applications`);
  },
  shortlistApplication: async function (applicationId) {
    return await api.put("/admin/application/" + applicationId + "/shortlist");
  },
  rejectApplication: async function (applicationId) {
    return await api.put("/admin/application/" + applicationId + "/reject");
  },
  getDashboard: async function () {
    return await api.get("/hr/dashboard");
  },
  createHrJob: async function (data) {
    return await api.post("/hr/create", data);
  },
  updateHrJob: async function (jobId, data) {
    return await api.put("/hr/update/" + jobId, data);
  },
  getHrJobApplications: (job_id) => api.get(`/hr/jobs/${job_id}/applications`),
  applyJob: async function (jobId, data) {
    return await api.post("/student/apply/" + jobId, data);
  },
  getStudentAppliedJobs: () => api.get(`/student/my-applications`),
};

/* =========================================================
   FORM DATA HELPER
========================================================= */

const toFormData = function (data) {
  const formData = new FormData();
  Object.keys(data).forEach(function (key) {
    formData.append(key, data[key]);
  });
  return formData;
};

/* =====================================================
   EVENTS APIs
===================================================== */

const EventAPI = {
  getHrEvents: async function () {
    return await api.get("/hr/events");
  },
  getStudentEvents: async function () {
    return await api.get("/student/events");
  },
  getEventRegistrations: async function (event_id) {
    return await api.get(`/admin/event/${event_id}/registrations`);
  },
  createEvents: async function (data) {
    return await api.post("/admin/events/create", data);
  },
  getAdminEvents: async function () {
    return await api.get("/admin/events");
  },
  getAllRegistrations: async function () {
    return await api.get("/admin/registrations");
  },
};

/* =========================================================
   USER APIs
========================================================= */

const UserAPI = {
  registerUser: async function (data) {
    return await api.post("/registration/register", data);
  },
};

/* =========================================================
   REGISTRATION APIs
========================================================= */

const RegistrationAPI = {
  getEventRegistrations: (eventId) => api.get(`/admin/event/${eventId}/registrations`),
  approveRegistration: (id) => api.put(`/admin/event-registrations/${id}/approve`),
  rejectRegistration: (id) => api.put(`/admin/event-registrations/${id}/reject`),
  deleteRegistration: (id) => api.delete(`/admin/event-registrations/${id}`),
  registerForTraining: (payload) => api.post("/registration/training/register", payload),
  approveTrainingRegistration: (id) => api.put(`/admin/event-registrations/${id}/approve`),
  rejectTrainingRegistration: (id) => api.put(`/admin/event-registrations/${id}/reject`),
  deleteTrainingRegistration: (id) => api.delete(`/admin/event-registrations/${id}`),
};

/* =========================================================
   TRAINING APIs
========================================================= */

const TrainingAPI = {
  createTraining: (payload) => api.post(`/admin/training/create`, payload),
  updateTraining: (id, payload) => api.put(`/admin/training/${id}`, payload),
  getAllTrainings: () => api.get(`/admin/training-programs`),
  getTrainingById: (id) => api.get(`/admin/training/${id}`),
  getHrTrainings: () => api.get("/hr/training-programs"),
  getTrainingRegistrations: (id) => api.get(`/admin/training/${id}/registrations`),
  getStudentTrainings: () => api.get("/student/training-programs"),
};

/* =========================================================
   BOARD MEMBERS APIs
========================================================= */

const BoardMembersAPI = {
  getAllBoardMembers: () => api.get(`/admin/board-members`),
  getBoardMember: (member_id) => api.get(`/admin/board-members/${member_id}`),
  createBoardMember: (payload) => api.post(`/admin/board-members`, payload),
  updateBoardMember: (member_id, payload) => api.put(`/admin/board-members/${member_id}`, payload),
  deleteBoardMember: (member_id) => api.delete(`/admin/board-members/${member_id}`),
};

/* =========================================================
   BLACK PROFILES APIs
========================================================= */

const BlackProfilesAPI = {
  createBlackProfile: (payload) => api.post(`/admin/admin/black-profiles`, payload),
  getAllBlackProfiles: () => api.get(`/admin/black-profiles`),
  updateBlackProfile: (profile_id, payload) => api.put(`/admin/black-profiles/${profile_id}`, payload),
  deleteBlackProfile: (profile_id) => api.delete(`/admin/black-profiles/${profile_id}`),
  uploadDocument: (formData) => api.post(`/black_profiles/admin/upload-black-profile-document`, formData),
  getDocument: (profile_id) => api.get(`/black_profiles/admin/black-profiles/${profile_id}/document`),
};

/* =========================================================
   JOB FAIR APIs
========================================================= */

const JobFairAPI = {
  createJobFair: (payload) => api.post(`/admin/admin/job-fairs/create`, payload),
  getAllJobFairs: () => api.get(`/admin/admin/job-fairs`),
  getJobFairRegistrations: (job_fair_id) => api.get(`/admin/job-fair/${job_fair_id}/registrations`),
  deleteJobFair: (job_fair_id) => api.delete(`/admin/admin/job-fairs/${job_fair_id}`),
  getHrJobFairs: () => api.get(`/hr/job-fairs`),
  getStudentJobFairs: () => api.get(`/student/job-fairs`),
  registerHrForJobFair: (payload) => api.post(`/hr/job-fairs/hr/register`, payload),
  registerStudentForJobFair: (payload) => api.post(`/student/job-fairs/student/register`, payload),
  approveStudentRegistration: (registration_id) =>
    api.put(`/admin/job-fairs/student-registration/${registration_id}/approve`),
  rejectStudentRegistration: (registration_id) =>
    api.put(`/admin/job-fairs/student-registration/${registration_id}/reject`),
  approveHrRegistration: (registration_id) =>
    api.put(`/admin/job-fairs/hr-registration/${registration_id}/approve`),
  rejectHrRegistration: (registration_id) =>
    api.put(`/admin/job-fairs/hr-registration/${registration_id}/reject`),
};

/* =========================================================
   HR BLACK PROFILES APIs
========================================================= */

const HrBlackProfilesAPI = {
  getAllBlackProfiles: () => api.get(`/hr/hr/black-profiles`),
  createBlackProfile: (payload) => api.post(`/hr/hr/black-profiles`, payload),
  getBlackProfileById: (profile_id) => api.get(`/hr/hr/black-profiles/${profile_id}`),
  updateBlackProfile: (profile_id, payload) => api.put(`/hr/hr/black-profiles/${profile_id}`, payload),
  deleteBlackProfile: (profile_id) => api.delete(`/hr/hr/black-profiles/${profile_id}`),
  uploadDocument: (formData) => api.post(`/black_profiles/hr/upload-black-profile-document`, formData),
  getDocument: (profile_id) => api.get(`/black_profiles/hr/black-profiles/${profile_id}/document`),
};

/* =========================================================
   TALENT PUBLICATIONS APIs
========================================================= */

const TalentPublicationsAPI = {
  getAllPublications: () => api.get(`/admin/talent-publications`),
  createPublication: (payload) => api.post(`/admin/talent-publications`, payload),
  getPublicationById: (publication_id) => api.get(`/admin/talent-publications/${publication_id}`),
  updatePublication: (publication_id, payload) => api.put(`/admin/talent-publications/${publication_id}`, payload),
  deletePublication: (publication_id) => api.delete(`/admin/talent-publications/${publication_id}`),
  getConfig: () => api.get(`/admin/talent-publications/config`),
  saveConfig: (payload) => api.post(`/admin/talent-publications/config`, payload),
  getPublicPublications: () => api.get(`/talent-publications`),
  getLandingData: () => api.get(`/landing/talent-publications`),
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/talent_publications/image`, fd);
  },
  uploadPdf: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/talent_publications/pdf`, fd);
  },
  getAllImages: () => api.get(`/talent_publications/images`),
  getAllPdfs: () => api.get(`/talent_publications/pdfs`),
  getAllFiles: () => api.get(`/talent_publications/files`),
};

/* =========================================================
   HR PROFILE APIs
========================================================= */

const HrProfileAPI = {
  getProfile: () => api.get("/hr/profile"),
  updateProfile: (payload) => api.put("/hr/profile", payload),
};

/* =========================================================
   STUDENT PROFILE APIs
========================================================= */

const StudentProfileAPI = {
  getProfile: () => api.get("/student/profile"),
  updateProfile: (payload) => api.put("/student/profile", payload),
};

/* =========================================================
   EXPORT
========================================================= */

const ApiService = {
  AuthAPI,
  OtpAPI,
  PasswordAPI,
  MembershipAPI,
  AdminAPI,
  ContactAPI,
  UploadAPI,
  JobAPI,
  UserAPI,
  EventAPI,
  RegistrationAPI,
  TrainingAPI,
  BoardMembersAPI,
  BlackProfilesAPI,
  JobFairAPI,
  HrBlackProfilesAPI,
  TalentPublicationsAPI,
  HrProfileAPI,
  StudentProfileAPI,
  toFormData,
};

export default ApiService;